// Scan history — table columns, status filter, chip map.

import { SCAN_STATUS, SCAN_TYPE } from 'src/services/consts/shared.const'

export const SCAN_HISTORY_TABLE_COLUMN_NAMES = {
  DATE: 'date',
  STATUS: 'status',
  PAGES: 'pages',
  TRACKERS: 'trackers',
}

export const SCAN_HISTORY_TABLE_COLUMNS = [
  {
    name: SCAN_HISTORY_TABLE_COLUMN_NAMES.DATE,
    label: 'Date',
    field: 'completedAt',
    align: 'left',
    sortable: true,
    classes: 'scan-date main-td',
    headerClasses: 'scan-date main-td',
  },
  {
    name: SCAN_HISTORY_TABLE_COLUMN_NAMES.PAGES,
    label: 'Pages',
    field: 'pagesScanned',
    align: 'center',
    sortable: true,
    classes: 'scan-pages sm-td',
    headerClasses: 'scan-pages sm-td',
  },
  {
    name: SCAN_HISTORY_TABLE_COLUMN_NAMES.TRACKERS,
    label: 'Trackers',
    field: 'totalItems',
    align: 'center',
    sortable: false,
    classes: 'scan-trackers lg-td',
    headerClasses: 'scan-trackers lg-td',
  },
  {
    name: SCAN_HISTORY_TABLE_COLUMN_NAMES.STATUS,
    label: 'Status',
    field: 'status',
    align: 'center',
    sortable: true,
    classes: 'scan-status md-td',
    headerClasses: 'scan-status md-td',
  },
]

/** Namespaced URL keys for the History table (shared `/cookies-trackers` route). */
export const SCAN_HISTORY_QUERY_KEYS = {
  scanStatus: 'scanStatus',
  dateRange: 'dateRange',
  sortBy: 'historySortBy',
  descending: 'historyDescending',
  page: 'historyPage',
  rowsPerPage: 'historyRowsPerPage',
}

/** Sortable History column `name` values accepted from `historySortBy`. */
export const VALID_SCAN_HISTORY_SORT_BY = new Set(
  SCAN_HISTORY_TABLE_COLUMNS.filter((column) => column.sortable).map((column) => column.name)
)

export const SCAN_HISTORY_DEFAULT_SORT = {
  sortBy: SCAN_HISTORY_TABLE_COLUMN_NAMES.DATE,
  descending: true,
  page: 1,
  rowsPerPage: 10,
}

/** Sidebar "All scans" value and URL default when no status filter is active. */
export const SCAN_HISTORY_STATUS_ALL_KEY = 'all'

/** Sidebar group key for pending and scanning scans (combined in-progress filter). */
export const SCAN_HISTORY_STATUS_ACTIVE_KEY = 'in-progress'

export const SCAN_HISTORY_DEFAULT_FILTER = {
  dateRange: null,
  scanStatus: SCAN_HISTORY_STATUS_ALL_KEY,
}

/** Combined filter + sort defaults keyed as URL params for `sanitizeQuery`. */
export const SCAN_HISTORY_QUERY_DEFAULTS = {
  [SCAN_HISTORY_QUERY_KEYS.scanStatus]: SCAN_HISTORY_DEFAULT_FILTER.scanStatus,
  [SCAN_HISTORY_QUERY_KEYS.dateRange]: SCAN_HISTORY_DEFAULT_FILTER.dateRange,
  [SCAN_HISTORY_QUERY_KEYS.sortBy]: SCAN_HISTORY_DEFAULT_SORT.sortBy,
  [SCAN_HISTORY_QUERY_KEYS.descending]: SCAN_HISTORY_DEFAULT_SORT.descending,
  [SCAN_HISTORY_QUERY_KEYS.page]: SCAN_HISTORY_DEFAULT_SORT.page,
  [SCAN_HISTORY_QUERY_KEYS.rowsPerPage]: SCAN_HISTORY_DEFAULT_SORT.rowsPerPage,
}

/** Lucide icon names for the Scan History status sidebar (matches directory-nav pattern). */
export const SCAN_HISTORY_STATUS_FILTER_ICONS = {
  [SCAN_HISTORY_STATUS_ALL_KEY]: 'history',
  [SCAN_STATUS.COMPLETED]: 'circle-check',
  [SCAN_STATUS.FAILED]: 'circle-x',
  [SCAN_HISTORY_STATUS_ACTIVE_KEY]: 'loader',
}

/** All valid sidebar status keys for URL ?scanStatus validation (excludes "all"). */
export const VALID_SCAN_HISTORY_STATUS_KEYS = new Set([
  SCAN_STATUS.COMPLETED,
  SCAN_STATUS.FAILED,
  SCAN_HISTORY_STATUS_ACTIVE_KEY,
])

/**
 * Scan status chip for the scan history table status column.
 * @type {Record<string, { label: string, chipClass: string }>}
 */
export const SCAN_HISTORY_STATUS_CHIP_MAP = {
  [SCAN_STATUS.COMPLETED]: {
    label: 'Completed',
    chipClass: 'bg-positive-light text-positive',
  },
  [SCAN_STATUS.FAILED]: {
    label: 'Failed',
    chipClass: 'bg-negative-light text-negative',
  },
  [SCAN_STATUS.PENDING]: {
    label: 'Scanning',
    chipClass: 'bg-brand-extra-light text-brand',
  },
  [SCAN_STATUS.SCANNING]: {
    label: 'Scanning',
    chipClass: 'bg-brand-extra-light text-brand',
  },
}

/**
 * Scan type badge for the scan history date cell.
 * @type {Record<string, { label: string, chipClass: string, icon: string }>}
 */
export const SCAN_HISTORY_TYPE_CHIP_MAP = {
  [SCAN_TYPE.PREVIEW]: {
    label: 'Preview',
    chipClass: 'bg-cta-light text-cta-dark',
    icon: 'zap',
  },
  [SCAN_TYPE.FULL]: {
    label: 'Full',
    chipClass: 'bg-brand-extra-light text-brand',
    icon: 'scan-search',
  },
}

/** BasicNoData config variants for the scan history table (banner detail). */
export const NO_DATA_SCAN_HISTORY = {
  empty: {
    icon: 'scan-search',
    title: 'No scan history.',
    message: 'Run a scan to start building your scan history.',
  },
  filtered: {
    icon: 'package-open',
    title: 'No scans with this status.',
    message: 'Try selecting a different status.',
    clearActionLabel: 'Show all scans',
  },
  dateFiltered: {
    icon: 'funnel-x',
    title: 'No scans match the selected date range.',
    message: 'Try selecting a different date range.',
    clearActionLabel: 'Clear date filter',
  },
}
